// The treaty drawer: read on the Overview, work on the other three tabs.
//
// A component rather than a render function, because it owns state — the
// share panel and the layer stack are edited locally and saved whole. A
// render prop called from inside ModulePage's JSX would be putting its hooks
// on ModulePage, and the first conditional hook would break the order.

import React, { useEffect, useState } from 'react';
import {
  C, Icons, Pill, Panel, Row, MiniTable, NextAction, money0, pct, date, humanise,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, RowEditor,
  EditPanel, StatusAction, RemoveAction,
} from './actions';

const STRUCTURE_LABELS = {
  quota_share: 'Quota Share', surplus: 'Surplus Share',
  excess_of_loss: 'Excess of Loss', stop_loss: 'Stop Loss', facultative: 'Per Risk',
};

// Local copies, re-seeded whenever a different record is opened. Without the
// row id in the dependency, opening a second treaty would show the first
// one's shares still sitting in the editor.
const useLocalRows = (source, id) => {
  const [rows, setRows] = useState(source || []);
  // Keyed on the record id, NOT on `source`: the array identity changes on
  // every list refresh, and re-seeding on that would wipe an edit in
  // progress the moment anything else saved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(source || []); }, [id]);
  return [rows, setRows];
};

export default function TreatyDrawer({ row: r, tab, reload }) {
  const act = useAction(reload);
  const [shares, setShares] = useLocalRows(r.shares, r._id);
  const [layers, setLayers] = useLocalRows(r.layers, r._id);

  const signed = shares.reduce((a, x) => a + (Number(x.signedLine) || 0), 0);
  const live = r.status === 'active' || r.status === 'bound';

  // ─── Participants ───
  if (tab === 'Participants') {
    return (
      <Panel title='Market participation'>
        <Notice error={act.error} note={act.note} />
        <RowEditor
          columns={[
            { key: 'name', label: 'Market', placeholder: 'Swiss Re' },
            { key: 'writtenLine', label: 'Written %', numeric: true },
            { key: 'signedLine', label: 'Signed %', numeric: true },
          ]}
          rows={shares}
          onChange={setShares}
          blank={{ name: '', writtenLine: '', signedLine: '', role: 'following', status: 'quoted' }}
          addLabel='+ Add market'
          saving={act.saving}
          total={{
            label: 'Total signed',
            value: `${Math.round(signed * 100) / 100}%`,
            // 100% exactly is the only finished answer. Over means the
            // programme has sold more than exists; under means capacity is
            // still open. Zero is a programme nobody has approached, which is
            // a legitimate starting state rather than an error.
            ok: Math.abs(signed - 100) < 0.01 || signed === 0,
            warning: signed > 100
              ? `Over-placed by ${Math.round((signed - 100) * 100) / 100}% — sign down before binding.`
              : `${Math.round((100 - signed) * 100) / 100}% still open to place.`,
          }}
          footer={(
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              Written is what the market offered; signed is what it took after signing
              down. They differ, and every allocation follows the signed line.
            </div>
          )}
          onSave={() => act.run({
            method: 'put',
            url: `/reinsurance/treaties/${r._id}/shares`,
            body: {
              shares: shares
                .filter(x => String(x.name || '').trim())
                .map(x => ({
                  name: String(x.name).trim(),
                  writtenLine: Number(x.writtenLine) || 0,
                  signedLine: Number(x.signedLine) || 0,
                  role: x.role || 'following',
                  status: x.status || 'quoted',
                  currency: r.currency,
                })),
            },
            success: 'Share panel saved.',
          })}
        />
      </Panel>
    );
  }

  // ─── Layers ───
  if (tab === 'Layers') {
    return (
      <Panel title='Layer structure'>
        <Notice error={act.error} note={act.note} />
        <RowEditor
          columns={[
            { key: 'number', label: 'Layer', numeric: true },
            { key: 'attachment', label: 'Attachment', numeric: true },
            { key: 'limit', label: 'Limit', numeric: true },
            { key: 'premium', label: 'Premium', numeric: true, wide: true },
          ]}
          rows={layers}
          onChange={setLayers}
          blank={{ number: layers.length + 1, attachment: '', limit: '', premium: '', status: 'active' }}
          addLabel='+ Add layer'
          saving={act.saving}
          total={{
            label: 'Total limit',
            value: money0(layers.reduce((a, l) => a + (Number(l.limit) || 0), 0), r.currency),
            ok: true,
          }}
          footer={(
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              Attachment is where a layer starts responding; limit is how much it pays
              above that. Proportional programmes have no layers.
            </div>
          )}
          onSave={() => act.run({
            method: 'patch',
            url: `/reinsurance/treaties/${r._id}`,
            body: {
              layers: layers.map((l, i) => ({
                number: Number(l.number) || i + 1,
                attachment: Number(l.attachment) || 0,
                limit: Number(l.limit) || 0,
                premium: Number(l.premium) || 0,
                status: l.status || 'active',
              })),
            },
            success: 'Layer structure saved.',
          })}
        />
      </Panel>
    );
  }

  // ─── Documents ───
  if (tab === 'Documents') {
    return (
      <Panel title='Documents'>
        {(r.documents || []).length === 0
          ? <div style={{ fontSize: 12.5, color: C.faint }}>No documents uploaded.</div>
          : (r.documents || []).map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, padding: '6px 0', alignItems: 'center' }}>
              <span style={{ color: C.faint }}>{Icons.doc()}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.ink }}>
                {d.label || d.filename}
                <span style={{ display: 'block', fontSize: 11, color: C.muted }}>Added {date(d.uploadedAt)}</span>
              </span>
            </div>
          ))}
      </Panel>
    );
  }

  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Cedant' value={r.cedant?.name} />
        <Row label='Program type' value={r.kind === 'facultative' ? 'Facultative' : 'Treaty'} />
        <Row label='Line of business' value={r.classOfBusiness} />
        <Row label='Structure' value={STRUCTURE_LABELS[r.type] || humanise(r.type)} />
        <Row label='Territory' value={r.territory} />
        <Row label='Inception date' value={date(r.effectiveDate)} />
        <Row label='Expiry date' value={r.continuous ? 'Continuous' : date(r.expiryDate)} />
        <Row label='Currency' value={r.currency} />
        <Row label='Estimated premium' value={money0(r.premium?.estimated, r.currency)} />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
        <Row label='Last updated' value={date(r.updatedAt)} />
      </Panel>

      <Panel title='Layer summary'>
        <MiniTable
          head={['Layer', 'Attachment', 'Limit', 'Status']}
          rows={(r.layers || []).slice(0, 4).map(l => [
            l.number ?? '—', money0(l.attachment, r.currency), money0(l.limit, r.currency), humanise(l.status),
          ])}
          empty='This programme is not layered.'
        />
      </Panel>

      <Panel
        title='Market participation'
        action={<span style={{ fontSize: 12, color: r.signedLineTotal > 100 ? C.red : C.muted, fontWeight: 600 }}>
          {pct(r.signedLineTotal, 1)} signed
        </span>}
      >
        <MiniTable
          head={['Market', 'Signed line', 'Share']}
          rows={(r.shares || []).map(s => [
            s.name || '—',
            money0((r.premium?.estimated || 0) * ((s.signedLine || 0) / 100), r.currency),
            pct(s.signedLine, 0),
          ])}
          empty='No markets on this treaty yet — add them on the Participants tab.'
        />
      </Panel>

      {/* ── Actions ──
          Binding is the one irreversible step on this screen, so it asks
          first and says what it is about to do. The server refuses a second
          bind with a 409, which surfaces as an answer rather than an error. */}
      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/treaties'
            onSaved={reload}
            fields={[
            { key: 'name', label: 'Programme name' },
            { key: 'classOfBusiness', label: 'Class of business' },
            { key: 'territory', label: 'Territory' },
            { key: 'effectiveDate', label: 'Inception', type: 'date' },
            { key: 'expiryDate', label: 'Expiry', type: 'date' },
            { key: 'currency', label: 'Currency' },
            ]}
          />
          <StatusAction
            record={r}
            endpoint='/reinsurance/treaties'
            onSaved={reload}
            options={[{ value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'quoted', label: 'Quoted' }, { value: 'bound', label: 'Bound' }, { value: 'active', label: 'Active' }, { value: 'expired', label: 'Expired' }, { value: 'run_off', label: 'Run-off' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/treaties' noun='treaty' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          {!live ? (
            <ConfirmAction
              label='Bind treaty'
              question={`Bind ${r.treatyNumber} with ${Math.round(r.signedLineTotal || 0)}% signed? This fixes the programme and its market lines.`}
              confirmLabel='Bind'
              busy={act.saving}
              onConfirm={() => act.run({
                url: `/reinsurance/treaties/${r._id}/bind`,
                success: 'Treaty bound.',
              })}
            />
          ) : (
            <ActionButton
              tone='quiet'
              busy={act.saving}
              onClick={() => act.run({
                method: 'patch',
                url: `/reinsurance/treaties/${r._id}`,
                body: { status: 'run_off' },
                success: 'Treaty moved to run-off.',
              })}
            >
              Move to run-off
            </ActionButton>
          )}
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              method: 'patch',
              url: `/reinsurance/treaties/${r._id}`,
              body: { riskFlag: r.riskFlag === 'high' ? 'medium' : 'high' },
              success: 'Risk flag updated.',
            })}
          >
            {r.riskFlag === 'high' ? 'Lower risk flag' : 'Flag as high risk'}
          </ActionButton>
        </ActionRow>
      </Panel>

      {r.nextAction?.label && (
        <NextAction label={r.nextAction.label} detail={r.nextAction.detail} />
      )}
    </>
  );
}
